import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Checkbox, Input, Stack } from "@mui/joy";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import LinkV2 from "components/common/LinkV2";
import { PATH } from "routes/paths";
import {
  ambassadorApi,
  setAmbassadorToken,
  type AmbassadorTokenResult,
} from "api/ambassadorApi";
import {
  formatPhoneNumberInput,
  isValidKrMobile,
  normalizePhoneNumber,
} from "utils/phoneNumberUtils";
import { resolveAmbassadorReturnTo } from "utils/returnToUtils";
import distancingLogo from "static/image/common/distancing_logo.png";

type Mode = "login" | "signup" | "verify" | "resetRequest" | "resetConfirm";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

// 백엔드 AmbassadorBO 상수와 맞춘 값 — 인증번호 유효 3분, 재발송 쿨다운 30초.
const VERIFICATION_TTL_MS = 3 * 60 * 1000;
const RESEND_COOLDOWN_MS = 30 * 1000;

// 독립 웹 페이지 입력 표준 (PromotionBookPhoneNumberSubmit 선례)
const inputSx = {
  width: "100%",
  height: "54px",
  borderRadius: "12px",
  backgroundColor: theme.vars.palette.Surface.Forward,
  fontSize: "16px",
  borderColor: "background.level3",
  borderWidth: "1px",
  "--joy-focus-thickness": "0px",
  boxShadow: "0px 0px 0px transparent",
  "&:focus-within": {
    boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`,
  },
};

// 레퍼럴 앰버서더(인플루언서) 전용 페이지 로그인/가입/전화번호(알림톡) 인증/비밀번호 재설정.
// login | signup | verify | resetRequest | resetConfirm 상태머신 단일 페이지 — verify 는 signup 직후에만 진입한다
// (인증 미완료 계정은 로그인 시 미가입으로 응답되어 가입부터 다시 진행하고, 재설정은 인증 완료 계정 전용이다).
export default function AmbassadorLogin() {
  const navigate = useNavigate();
  const location = useLocation();

  // 로그인 후 복귀 목적지 — 보호 페이지 가드가 state.from 으로, 401 인터셉터가 ?returnTo= 로 전달한다.
  // 가입→인증(자동 로그인) 경로에서도 유지된다 (모드 전환은 location 을 바꾸지 않는다).
  const returnTo = resolveAmbassadorReturnTo(
    (location.state as { from?: string } | null)?.from ??
      new URLSearchParams(location.search).get("returnTo")
  );

  const [mode, setMode] = useState<Mode>("login");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // 마지막 인증번호 발송 시각 — 가입/재발송 성공 시 기록. 3분 타이머와 재발송 쿨다운의 기준.
  const [codeSentAt, setCodeSentAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // 가입 시 앰버서더 이용약관 동의 (필수) — 체크해야 가입 버튼이 활성화된다.
  const [termsAgreed, setTermsAgreed] = useState(false);

  useEffect(() => {
    if (mode !== "verify" && mode !== "resetConfirm") return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [mode]);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setNotice(null);
    setVerificationCode("");
    setPasswordConfirm("");
    setTermsAgreed(false);
    // 재설정 진입 시 로그인 칸에 치던 비밀번호가 "새 비밀번호"로 끌려가지 않게 비운다
    if (next === "resetRequest") setPassword("");
  };

  const completeLogin = (result?: AmbassadorTokenResult) => {
    if (!result?.accessToken) {
      setError(FALLBACK_ERROR_MESSAGE);
      return;
    }
    setAmbassadorToken(result.accessToken);
    navigate(returnTo, { replace: true });
  };

  const submit = async (action: () => Promise<void>) => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = () =>
    submit(async () => {
      const res = await ambassadorApi.login(
        normalizePhoneNumber(phoneNumber),
        password
      );
      if (res.code === 0) {
        completeLogin(res.result);
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    });

  const handleSignup = () =>
    submit(async () => {
      const res = await ambassadorApi.signup(
        normalizePhoneNumber(phoneNumber),
        password
      );
      if (res.code === 0) {
        setMode("verify");
        setCodeSentAt(Date.now());
        setNotice(
          "인증번호를 카카오 알림톡으로 보냈어요. 3분 안에 입력해 주세요."
        );
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    });

  const handleVerify = () =>
    submit(async () => {
      const res = await ambassadorApi.verify(
        normalizePhoneNumber(phoneNumber),
        password,
        verificationCode
      );
      if (res.code === 0) {
        completeLogin(res.result);
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    });

  // 비밀번호 재설정 1단계 — 인증번호 발송 후 확인 단계로 전환
  const handleResetRequest = () =>
    submit(async () => {
      const res = await ambassadorApi.requestPasswordReset(
        normalizePhoneNumber(phoneNumber)
      );
      if (res.code === 0) {
        setMode("resetConfirm");
        setCodeSentAt(Date.now());
        setNotice(
          "인증번호를 카카오 알림톡으로 보냈어요. 3분 안에 입력해 주세요."
        );
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    });

  // 비밀번호 재설정 2단계 — 성공 시 자동 로그인 (returnTo 유지)
  const handleResetPassword = () =>
    submit(async () => {
      const res = await ambassadorApi.resetPassword(
        normalizePhoneNumber(phoneNumber),
        verificationCode,
        password
      );
      if (res.code === 0) {
        completeLogin(res.result);
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    });

  const handleResend = () =>
    submit(async () => {
      // 재설정 흐름의 재발송은 재설정 요청을 다시 보낸다 (resendVerification 은 미인증 계정 전용)
      const res =
        mode === "resetConfirm"
          ? await ambassadorApi.requestPasswordReset(
              normalizePhoneNumber(phoneNumber)
            )
          : await ambassadorApi.resendVerification(
              normalizePhoneNumber(phoneNumber)
            );
      if (res.code === 0) {
        setCodeSentAt(Date.now());
        setNotice("인증번호를 다시 보냈어요. 3분 안에 입력해 주세요.");
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    });

  const codeRemainingSeconds =
    codeSentAt === null
      ? null
      : Math.max(0, Math.ceil((codeSentAt + VERIFICATION_TTL_MS - now) / 1000));
  const resendRemainingSeconds =
    codeSentAt === null
      ? 0
      : Math.max(0, Math.ceil((codeSentAt + RESEND_COOLDOWN_MS - now) / 1000));
  const formatSeconds = (seconds: number) =>
    `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

  const phonePasswordFilled =
    isValidKrMobile(phoneNumber) && password.length > 0;
  const passwordConfirmMismatch =
    (mode === "signup" || mode === "resetConfirm") &&
    passwordConfirm.length > 0 &&
    password !== passwordConfirm;
  const canSubmit =
    !submitting &&
    (mode === "verify"
      ? verificationCode.trim().length > 0 && password.length > 0
      : mode === "signup"
        ? phonePasswordFilled &&
          password.length >= 8 &&
          password === passwordConfirm &&
          termsAgreed
        : mode === "resetRequest"
          ? isValidKrMobile(phoneNumber)
          : mode === "resetConfirm"
            ? verificationCode.trim().length > 0 &&
              password.length >= 8 &&
              password === passwordConfirm
            : phonePasswordFilled);

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (mode === "login") handleLogin();
    else if (mode === "signup") handleSignup();
    else if (mode === "resetRequest") handleResetRequest();
    else if (mode === "resetConfirm") handleResetPassword();
    else handleVerify();
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        backgroundColor: theme.vars.palette.Background.Level3,
      }}
    >
      <Box
        sx={{
          mx: "auto",
          width: "100%",
          maxWidth: "400px",
          px: "20px",
          py: "64px",
        }}
      >
        <Stack spacing={4} alignItems="center" sx={{ width: "100%" }}>
          <img
            src={distancingLogo}
            alt="distancing logo"
            style={{ width: "138px", height: "auto" }}
          />

          <Stack spacing={"12px"} alignItems="center" sx={{ width: "100%" }}>
            <CustomTypography
              variant="H3"
              sx={{
                textAlign: "center",
                fontWeight: 700,
                color: theme.vars.palette.Content.Strong,
              }}
            >
              {"앰버서더 전용 페이지"}
            </CustomTypography>
            <CustomTypography
              variant="Body"
              sx={{
                textAlign: "center",
                fontWeight: 500,
                color: theme.vars.palette.Content.Medium,
              }}
            >
              {mode === "login" && "앰버서더 계정으로 로그인해 주세요."}
              {mode === "signup" &&
                "전화번호 인증 후 가입할 수 있어요.\n인증번호는 카카오 알림톡으로 보내드려요."}
              {mode === "verify" &&
                "카카오 알림톡으로 받은 인증번호를 입력해 주세요."}
              {mode === "resetRequest" &&
                "가입한 전화번호로 인증번호를 보내드릴게요."}
              {mode === "resetConfirm" &&
                "인증번호 확인 후 새 비밀번호로 바꿔드릴게요."}
            </CustomTypography>
          </Stack>

          {/* 인풋 그룹과 CTA를 한 묶음으로 — 사이 간격은 8px (문구 슬롯이 이미 여백 역할) */}
          <Stack spacing={"8px"} sx={{ width: "100%" }}>
            <Stack spacing={"12px"} sx={{ width: "100%" }}>
              <Input
                sx={inputSx}
                type="tel"
                slotProps={{
                  input: { inputMode: "tel", autoComplete: "tel" },
                }}
                placeholder="전화번호 (010-1234-5678)"
                value={phoneNumber}
                disabled={mode === "verify" || mode === "resetConfirm"}
                onChange={(e) =>
                  setPhoneNumber(formatPhoneNumberInput(e.target.value))
                }
              />

              {mode !== "verify" && (
                <Input
                  sx={inputSx}
                  type="password"
                  slotProps={{
                    input: {
                      autoComplete:
                        mode === "signup" || mode === "resetConfirm"
                          ? "new-password"
                          : "current-password",
                    },
                  }}
                  placeholder={
                    mode === "signup"
                      ? "비밀번호 (8자 이상)"
                      : mode === "resetConfirm"
                        ? "새 비밀번호 (8자 이상)"
                        : "비밀번호"
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
              )}

              {(mode === "signup" || mode === "resetConfirm") && (
                <Input
                  sx={inputSx}
                  type="password"
                  slotProps={{ input: { autoComplete: "new-password" } }}
                  placeholder="비밀번호 확인"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
              )}

              {(mode === "verify" || mode === "resetConfirm") && (
                <Input
                  sx={inputSx}
                  type="text"
                  slotProps={{
                    input: {
                      inputMode: "numeric",
                      autoComplete: "one-time-code",
                      maxLength: 6,
                    },
                  }}
                  placeholder="인증번호 6자리"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                  endDecorator={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                    >
                      {codeRemainingSeconds !== null && (
                        <CustomTypography
                          variant="Caption"
                          sx={{
                            fontWeight: 500,
                            color: theme.vars.palette.Content.Medium,
                          }}
                        >
                          {formatSeconds(codeRemainingSeconds)}
                        </CustomTypography>
                      )}
                      {/* 재발송 아이콘 — 백엔드 30초 쿨다운 동안은 흐리게 + 비활성화해
                          "잠시 후 다시 발송할 수 있어요" 오류를 만나지 않게 한다 */}
                      <RefreshRoundedIcon
                        titleAccess="인증번호 재발송"
                        onClick={() => {
                          if (submitting || resendRemainingSeconds > 0) return;
                          handleResend();
                        }}
                        sx={{
                          fontSize: "20px",
                          color: theme.vars.palette.Content.Medium,
                          cursor:
                            submitting || resendRemainingSeconds > 0
                              ? "default"
                              : "pointer",
                          ...(resendRemainingSeconds > 0 && { opacity: 0.3 }),
                        }}
                      />
                    </Box>
                  }
                />
              )}

              {/* 안내/에러 문구 고정 슬롯 — 인풋 바로 아래에 붙이고, 문구 유무와 무관하게 높이를 유지해 CTA 버튼이 밀리지 않게 한다 */}
              <CustomTypography
                variant="Caption"
                sx={{
                  // 인풋 스택 기본 간격(12px)보다 인풋에 더 붙인다 — Stack spacing을 이기려면 !important 필요
                  marginTop: "4px !important",
                  minHeight: "20px",
                  textAlign: "left",
                  fontWeight: 500,
                  color:
                    passwordConfirmMismatch || error
                      ? theme.vars.palette.Content.HighlightWithAlert
                      : theme.vars.palette.Content.Highlight,
                }}
              >
                {passwordConfirmMismatch
                  ? "비밀번호가 일치하지 않아요."
                  : error || notice || ""}
              </CustomTypography>
            </Stack>

            <Stack spacing={"16px"} alignItems="center" sx={{ width: "100%" }}>
              {mode === "signup" && (
                // 약관 링크가 체크박스 label 안에 있으면 클릭이 토글로 흡수된다 — 링크를 label 밖으로 분리
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    justifyContent: "center",
                  }}
                >
                  <Checkbox
                    size="sm"
                    checked={termsAgreed}
                    onChange={(e) => setTermsAgreed(e.target.checked)}
                  />
                  <CustomTypography
                    variant="Caption"
                    sx={{
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Medium,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ textDecoration: "underline", cursor: "pointer" }}
                      onClick={() => window.open(PATH.ambassadorTerms)}
                    >
                      {"앰버서더 이용약관"}
                    </Box>
                    <Box
                      component="span"
                      sx={{ cursor: "pointer" }}
                      onClick={() => setTermsAgreed((v) => !v)}
                    >
                      {"에 동의합니다 (필수)"}
                    </Box>
                  </CustomTypography>
                </Box>
              )}
              <Button
                variant="Solid"
                size="lg"
                loading={submitting}
                disabled={!canSubmit}
                onClick={handleSubmit}
                sx={{
                  width: "100%",
                  height: "54px",
                  fontWeight: 700,
                  borderRadius: "12px",
                  fontSize: "16px",
                }}
              >
                {mode === "login"
                  ? "로그인"
                  : mode === "signup"
                    ? "가입하고 인증번호 받기"
                    : mode === "resetRequest"
                      ? "인증번호 받기"
                      : mode === "resetConfirm"
                        ? "비밀번호 재설정"
                        : "인증하고 시작하기"}
              </Button>

              {mode === "login" && (
                <Stack spacing={"10px"} alignItems="center">
                  <LinkV2
                    variant="Caption"
                    text="처음이신가요? 앰버서더 가입"
                    customSx={{ textDecoration: "underline" }}
                    onClick={() => switchMode("signup")}
                  />
                  <LinkV2
                    variant="Caption"
                    text="비밀번호를 잊으셨나요?"
                    customSx={{ textDecoration: "underline" }}
                    onClick={() => switchMode("resetRequest")}
                  />
                </Stack>
              )}
              {mode === "signup" && (
                <LinkV2
                  variant="Caption"
                  text="이미 계정이 있어요. 로그인"
                  customSx={{ textDecoration: "underline" }}
                  onClick={() => switchMode("login")}
                />
              )}
              {(mode === "verify" ||
                mode === "resetRequest" ||
                mode === "resetConfirm") && (
                <LinkV2
                  variant="Caption"
                  text="로그인으로 돌아가기"
                  customSx={{ textDecoration: "underline" }}
                  onClick={() => switchMode("login")}
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
